/**
 * The composition pieces every strategic analysis is built from.
 *
 * The five frameworks share a SHAPE — conclusion, visualisation, focused detail
 * — and nothing else. So what lives here is only the shape: a hero that states
 * a conclusion, a card that holds the selected detail, a grid of short
 * facts. The visualisation itself is different in each framework and is
 * deliberately NOT abstracted; flattening a quadrant, a force cross and a 2×3
 * factor grid into one generic card template is the failure this correction
 * exists to undo.
 *
 * NOTHING HERE INVENTS COPY. Every string these components render arrives as a
 * prop from a validated report field. A missing value renders as an omitted
 * subsection or an explicit unknown — never as filler that makes the layout
 * look complete.
 */

import React, { useState } from 'react';
import { Text, View, type LayoutChangeEvent, type ViewStyle } from 'react-native';
import { ChevronRight, ExternalLink } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AnalysisWidthProvider, useAnalysisVisuals } from './analysis-visuals';

/**
 * The conclusion, before anything technical.
 *
 * `headline` is optional because three of the five frameworks publish no
 * headline field. Where there is none, the label and body carry the section on
 * their own rather than a sentence being written to fill the slot.
 */
export function AnalysisHero({
  label,
  headline,
  body,
  badge,
  accessibilityLabel,
}: {
  label: string;
  headline?: string;
  body?: string;
  badge?: string;
  accessibilityLabel?: string;
}) {
  const v = useAnalysisVisuals();

  return (
    <View
      accessibilityRole="summary"
      accessibilityLabel={accessibilityLabel ?? `${label}. ${headline ?? ''} ${body ?? ''}`.trim()}
      className="rounded-3xl px-4 py-4 gap-2"
      style={{ backgroundColor: v.hero.background, overflow: 'hidden' }}
    >
      {/* Wraps rather than squeezes: at 390pt a long badge and a two-line
          headline cannot share a row, and shrinking either of them is worse
          than putting the badge on its own line. */}
      <View className="flex-row flex-wrap items-start" style={{ gap: 8 }}>
        {/* minWidth, not just flexBasis: with only a basis the copy column
            SHRINKS to make room for a long badge instead of pushing it onto its
            own line, and a three-word headline stacks one word per line. */}
        <View className="gap-1" style={{ flexGrow: 1, flexShrink: 1, flexBasis: 200, minWidth: 200 }}>
          <Text
            numberOfLines={2}
            className="text-[11px] font-semibold uppercase tracking-widest leading-4"
            style={{ color: v.hero.label }}
          >
            {label}
          </Text>
          {headline ? (
            <Text className="text-lg font-semibold leading-6" style={{ color: v.hero.title }}>
              {headline}
            </Text>
          ) : null}
        </View>
        {badge ? (
          <View
            className="rounded-full px-2.5 py-1 shrink-0"
            style={{ backgroundColor: v.hero.badgeBackground }}
          >
            <Text numberOfLines={2} className="text-[11px] font-semibold" style={{ color: v.hero.badgeText }}>
              {badge}
            </Text>
          </View>
        ) : null}
      </View>
      {body ? (
        <Text className="text-[13px] leading-5" style={{ color: v.hero.body }}>
          {body}
        </Text>
      ) : null}
    </View>
  );
}

/** The card that holds whatever the reader just selected on the visualisation. */
export function DetailCard({
  title,
  badge,
  badgeTone,
  children,
  accessibilityLabel,
}: {
  title: string;
  badge?: string;
  badgeTone?: { fore: string; back: string };
  children: React.ReactNode;
  accessibilityLabel?: string;
}) {
  const v = useAnalysisVisuals();

  return (
    <View
      // `polite` so a screen reader announces the new selection rather than
      // silently swapping the card underneath the reader.
      accessibilityLiveRegion="polite"
      accessibilityRole="summary"
      accessibilityLabel={accessibilityLabel}
      className="rounded-3xl p-4 gap-3"
      style={{
        backgroundColor: v.detail.background,
        borderWidth: 1,
        borderColor: v.detail.border,
        overflow: 'hidden',
      }}
    >
      <View className="flex-row items-center gap-2">
        <Text numberOfLines={2} className="flex-1 text-base font-semibold" style={{ color: v.colors.text.primary }}>
          {title}
        </Text>
        {badge ? (
          <View
            className="rounded-full px-2.5 py-1 shrink-0"
            style={{ backgroundColor: badgeTone?.back ?? v.colors.surface.secondary, maxWidth: '48%' }}
          >
            <Text
              numberOfLines={2}
              className="text-[11px] font-semibold leading-4"
              style={{ color: badgeTone?.fore ?? v.colors.text.secondary }}
            >
              {badge}
            </Text>
          </View>
        ) : null}
      </View>
      {children}
    </View>
  );
}

/**
 * A labelled block of prose.
 *
 * Used to keep an OBSERVATION and an INTERPRETATION visibly apart. Merging them
 * would present an inference as something a source said.
 */
export function LabelledBlock({
  label,
  children,
  tone,
}: {
  label: string;
  children: React.ReactNode;
  tone?: string;
}) {
  const v = useAnalysisVisuals();
  return (
    <View className="gap-1">
      {/* A label is a heading, not metadata. At tertiary grey these were the
          hardest text on the screen to read, which inverted the hierarchy:
          the caption under a figure was more legible than the name of it. */}
      <Text
        className="text-[11px] font-semibold uppercase tracking-wider"
        style={{ color: tone ?? v.colors.text.primary }}
      >
        {label}
      </Text>
      {typeof children === 'string'
        ? (
          <Text className="text-[13px] leading-5" style={{ color: v.colors.text.primary }}>
            {children}
          </Text>
        )
        : children}
    </View>
  );
}

/** A 2-up grid of short labelled facts. Wraps rather than truncating a pair. */
export function FactGrid({ items }: { items: { label: string; value: string }[] }) {
  const v = useAnalysisVisuals();
  if (items.length === 0) return null;
  return (
    <View className="flex-row flex-wrap" style={{ gap: 8 }}>
      {items.map((item, index) => (
        <View
          key={`fact-${index}`}
          className="rounded-2xl px-3 py-2.5"
          style={{ flexGrow: 1, flexBasis: '46%', minWidth: 132, backgroundColor: v.colors.surface.secondary }}
        >
          <Text
            numberOfLines={1}
            className="text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: v.colors.text.primary }}
          >
            {item.label}
          </Text>
          <Text numberOfLines={3} className="mt-1 text-[13px] font-semibold leading-4" style={{ color: v.colors.text.primary }}>
            {item.value}
          </Text>
        </View>
      ))}
    </View>
  );
}

/**
 * The link into the evidence for whatever is on screen.
 *
 * The count is the number of retained sources behind the selection, so a
 * selection with none renders nothing rather than a button that opens an empty
 * list.
 */
export function SourcesLink({
  count,
  onPress,
  label = 'supporting sources',
}: {
  count: number;
  onPress?: () => void;
  label?: string;
}) {
  const v = useAnalysisVisuals();
  if (count <= 0 || !onPress) return null;
  return (
    <View className="self-start rounded-xl overflow-hidden" style={{ minHeight: 44, justifyContent: 'center' }}>
      <AnimatedPressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`View ${count} ${label}`}
        className="flex-row items-center gap-2 py-2.5 pr-2"
      >
        <ExternalLink size={15} color={v.colors.primary[700]} />
        <Text numberOfLines={1} className="text-[13px] font-semibold" style={{ color: v.colors.primary[700] }}>
          View {count} {label}
        </Text>
      </AnimatedPressable>
    </View>
  );
}

/**
 * A delimited section body.
 *
 * `rounded-2xl p-4` on `surface.primary` with a hairline border — the card the
 * rest of the app uses, so an analysis reads as part of the product rather
 * than as a page of loose paragraphs. Every top-level section of every
 * framework sits in one, under a `SectionDivider` label.
 */
export function SectionCard({
  children,
  padded = true,
  accessibilityLabel,
}: {
  children: React.ReactNode;
  /** Off when the card holds its own already-padded rows. */
  padded?: boolean;
  accessibilityLabel?: string;
}) {
  const v = useAnalysisVisuals();
  return (
    <View
      accessibilityLabel={accessibilityLabel}
      className={`rounded-2xl gap-3 ${padded ? 'p-4' : 'p-2'}`}
      style={{
        backgroundColor: v.colors.surface.primary,
        borderWidth: 1,
        borderColor: v.colors.border.light,
        overflow: 'hidden',
      }}
    >
      {children}
    </View>
  );
}

/** A hairline between rows inside one card. */
export function RowDivider() {
  const v = useAnalysisVisuals();
  return <View style={{ height: 1, backgroundColor: v.colors.border.light }} />;
}

/** A section title with an optional right-hand note. */
export function SectionHeading({ title, note }: { title: string; note?: string }) {
  const v = useAnalysisVisuals();
  return (
    <View className="flex-row items-end gap-2">
      <Text numberOfLines={2} className="flex-1 text-[15px] font-semibold" style={{ color: v.colors.text.primary }}>
        {title}
      </Text>
      {note ? (
        <Text numberOfLines={1} className="text-[11px] shrink-0" style={{ color: v.colors.text.tertiary }}>
          {note}
        </Text>
      ) : null}
    </View>
  );
}

/** The small print under a visualisation. Always present, never load-bearing. */
export function Footnote({ children }: { children: string }) {
  const v = useAnalysisVisuals();
  return (
    <Text className="text-[11px] leading-4" style={{ color: v.colors.text.tertiary }}>
      {children}
    </Text>
  );
}

/**
 * The honest-absence block.
 *
 * Rendered wherever an approved visual element has no validated value behind
 * it. It says what is missing and why — a blank space would read as a bug and a
 * neutral placeholder would read as a finding.
 */
export function InsufficientEvidenceNote({ title, detail }: { title: string; detail: string }) {
  const v = useAnalysisVisuals();
  return (
    <View
      accessibilityRole="summary"
      accessibilityLabel={`${title}. ${detail}`}
      className="rounded-2xl p-4 gap-1.5"
      style={{ backgroundColor: v.colors.amber.light, overflow: 'hidden' }}
    >
      <Text className="text-[13px] font-semibold" style={{ color: v.colors.amber.main }}>
        {title}
      </Text>
      <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
        {detail}
      </Text>
    </View>
  );
}

/**
 * A compact secondary action.
 *
 * Every technical disclosure on this screen is one of these: reachable in one
 * tap, and never competing with the conclusion for attention.
 */
export function SecondaryAction({
  label,
  icon,
  onPress,
  count,
  full = false,
  style,
}: {
  label: string;
  icon: React.ReactNode;
  onPress: () => void;
  count?: number;
  /** A full-width row inside a card, rather than a standalone chip. */
  full?: boolean;
  style?: ViewStyle;
}) {
  const v = useAnalysisVisuals();
  return (
    <View
      className={full ? 'overflow-hidden' : 'rounded-2xl overflow-hidden'}
      style={{
        height: 48,
        ...(full
          ? { alignSelf: 'stretch' }
          : {
            flexGrow: 1,
            flexBasis: '46%',
            // Wide enough that "How this was derived" never becomes "How this
            // was …". At 390pt two of these no longer fit a row, so they stack.
            minWidth: 176,
            backgroundColor: v.colors.surface.secondary,
          }),
        ...style,
      }}
    >
      <AnimatedPressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={label}
        className="flex-row items-center gap-2.5 px-3"
        style={{ flex: 1, minHeight: 48 }}
      >
        {icon}
        <Text numberOfLines={1} className="flex-1 text-[13px] font-semibold" style={{ color: v.colors.text.primary }}>
          {label}
        </Text>
        {typeof count === 'number' && count > 0 ? (
          <View className="rounded-full px-1.5 shrink-0" style={{ backgroundColor: v.colors.amber.light }}>
            <Text className="text-[11px] font-semibold" style={{ color: v.colors.amber.main }}>
              {count}
            </Text>
          </View>
        ) : null}
        <ChevronRight size={15} color={v.colors.text.tertiary} />
      </AnimatedPressable>
    </View>
  );
}

/**
 * Measures the width the analysis actually gets, and publishes it.
 *
 * THE CONTAINER, NOT THE WINDOW. On web the analysis renders inside
 * `WebAppShell`: a 260pt sidebar plus 20pt of padding either side, so a 1024pt
 * window leaves roughly 724pt of content. The layout used to compare the WINDOW
 * against the two-column threshold, so that 724pt column was handed a
 * side-by-side composition — the market chart and the assumptions panel in
 * ~350pt each, headings truncated, figures wrapped.
 *
 * Measuring is also what keeps the first paint honest. There is no guess here:
 * before `onLayout` the width is `null` and every consumer reads narrow, so the
 * server render and the first client render agree and hydration has nothing to
 * reconcile. The upgrade to two columns happens on the layout pass that knows
 * the real number.
 *
 * A width of 0 is IGNORED. React Native fires a zero-width layout for a view
 * that is not yet displayed, and taking it would publish "measured, and narrow"
 * — indistinguishable from a real narrow container and impossible to recover
 * from if no further layout arrives.
 */
export function AnalysisWidthBoundary({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: ViewStyle;
}) {
  const [width, setWidth] = useState<number | null>(null);

  const onLayout = (event: LayoutChangeEvent) => {
    const next = event.nativeEvent.layout.width;
    if (next > 0 && next !== width) setWidth(next);
  };

  return (
    <View onLayout={onLayout} style={style}>
      <AnalysisWidthProvider value={width}>{children}</AnalysisWidthProvider>
    </View>
  );
}
